import { InternalApiAlertType, InternalApiEntityType, InternalApiScreeningAlertStatus } from '../../api-internal';
import { DropdownOption } from '@salvapp/ui-components/build/dropdown/dropdown.types';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppState } from '../../store/store';
import { fetchAlertStatuses } from './alertStatusesActions';
import { ApiSanctionType } from '../../api';

export function toDropDownOptions(statuses: InternalApiScreeningAlertStatus[]): DropdownOption[] {
  return statuses.map((status) => ({
    key: status.id!.toString(),
    label: status.textName,
    value: status.code,
  }));
}

export function displayApplicable(
  entityType: InternalApiEntityType,
  statuses: InternalApiScreeningAlertStatus[],
  alertType: InternalApiAlertType,
  showSystem: boolean,
  alertSubType?: string,
  customListId?: string
) {
  return sortByAlertType(
    filterByAlertType(statuses, alertType, alertSubType, customListId)
      .filter((status) => {
        return showSystem || !status.isSystem;
      })
      .filter((status) => {
        return status._configuration.entityTypes?.includes(entityType);
      }),
    alertType
  );
}

export function filterByAlertType_DEPRECATED(statuses: InternalApiScreeningAlertStatus[], type: InternalApiAlertType) {
  return statuses.filter((status) => {
    switch (type) {
      case InternalApiAlertType.MONITORING:
        return status.enabledMonitoring;
      case InternalApiAlertType.SCREENING:
        return status.enabledScreening;
      case InternalApiAlertType.BRIDGE:
        return status.enabledBridge;
      case InternalApiAlertType.MANUAL:
        return !!status.enabledManual;
    }
  });
}

export function filterByAlertType(
  statuses: InternalApiScreeningAlertStatus[],
  type: InternalApiAlertType,
  subtype?: string,
  customListId?: string
) {
  return statuses.filter((status) => {
    const config = status._configuration!;
    switch (type) {
      case InternalApiAlertType.SCREENING:
        if (subtype === ApiSanctionType.CUSTOM_SCREENING_LIST) {
          return (
            !customListId ||
            (config.customLists?.enabled &&
              (!config.customLists.restrictedTo ||
                config.customLists.restrictedTo.length == 0 ||
                config.customLists.restrictedTo.includes(customListId)))
          );
        } else {
          return (
            config.screeningLists?.enabled &&
            (!subtype ||
              !config.screeningLists?.restrictedTo ||
              config.screeningLists.restrictedTo.length == 0 ||
              config.screeningLists.restrictedTo.includes(subtype))
          );
        }
      case InternalApiAlertType.MONITORING:
        return (
          config.monitoring?.enabled &&
          (!subtype ||
            !config.monitoring?.restrictedTo ||
            config.monitoring.restrictedTo.length == 0 ||
            config.monitoring.restrictedTo.includes(subtype))
        );
      case InternalApiAlertType.BRIDGE:
        return config.bridge?.enabled;
      case InternalApiAlertType.MANUAL:
        return status.enabledManual;
    }
  });
}

export function sortByAlertType(statuses: InternalApiScreeningAlertStatus[], type: InternalApiAlertType) {
  return statuses.sort((a, b) => {
    const configA = a._configuration!;
    const configB = b._configuration!;
    switch (type) {
      case InternalApiAlertType.SCREENING:
        return configA.screeningLists!.sortOrder! - configB.screeningLists!.sortOrder!;
      case InternalApiAlertType.MONITORING:
        return configA.monitoring!.sortOrder! - configB.monitoring!.sortOrder!;
      case InternalApiAlertType.BRIDGE:
        return configA.bridge!.sortOrder! - configB.bridge!.sortOrder!;
      case InternalApiAlertType.MANUAL:
        return (configA.screeningLists?.sortOrder || 0) - (configB.screeningLists?.sortOrder || 0);
    }
  });
}

export function useAlertStatuses() {
  const dispatch = useDispatch();
  const statuses = useSelector((state: AppState) => state.screeningAlertStatuses);
  const fetchStatuses = useCallback(() => {
    dispatch(fetchAlertStatuses());
  }, [dispatch]);
  useEffect(() => {
    if (statuses.statuses.length == 0) {
      fetchStatuses();
    }
  }, [statuses, fetchStatuses]);

  return [
    statuses.activeStatuses,
    statuses.statuses,
    statuses.deactivatedUniqueStatuses,
    fetchStatuses,
    statuses.hasFetchedStatuses,
  ] as const;
}
